import posthog from 'posthog-js';

import { normalizeApiPathForSpanName } from 'src/utils/apiSpanName';

// TEMPORARY: remove once the source of the stackless "Object captured as
// exception with keys: error, isUnhandledError, meta" PostHog issue is known.
// It records which browser path (window error vs unhandled rejection) still
// carries a non-Error value after the unhandled-rejection guard has run. Read-only:
// listeners never preventDefault or stop propagation.

export const EXCEPTION_PATH_PROBE_EVENT = 'exception_path_probe';

// Sampled once per page load so a burst from one load stays whole.
const PAGE_LOAD_SAMPLE_RATE = 0.25;
const MAX_PROBES_PER_PAGE_LOAD = 5;
const MAX_STACK_CHARS = 300;
const MAX_KEYS = 8;
const FLUSH_RETRY_MS = 2000;
const MAX_FLUSH_RETRIES = 10;
const LIFECYCLE_KEYS = ['error', 'isUnhandledError', 'meta'];

type ProbeSource = 'error' | 'unhandledrejection';

type Probe = {
  source: ProbeSource;
  default_prevented: boolean;
  value_type: string;
  keys: string[] | null;
  is_query_lifecycle_shape: boolean;
  stack: string | null;
  route: string;
};

const INSTALLED_FLAG = '__assemblyExceptionPathProbe';

// Keys, constructor and a short stack only. Never the value itself: request
// metadata on these objects can carry URLs and user data.
const describe = (
  source: ProbeSource,
  value: unknown,
  defaultPrevented: boolean,
): Probe => {
  const isObject = typeof value === 'object' && value !== null;
  const keys = isObject ? Object.keys(value as object) : null;
  const stack =
    isObject && typeof (value as { stack?: unknown }).stack === 'string'
      ? (value as { stack: string }).stack.slice(0, MAX_STACK_CHARS)
      : null;

  return {
    source,
    default_prevented: defaultPrevented,
    value_type: isObject
      ? ((value as object).constructor?.name ?? 'Object')
      : typeof value,
    keys: keys ? keys.slice(0, MAX_KEYS) : null,
    is_query_lifecycle_shape:
      keys !== null && LIFECYCLE_KEYS.every((key) => keys.includes(key)),
    stack,
    route: normalizeApiPathForSpanName(window.location.pathname),
  };
};

export const installExceptionPathProbe = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const probeWindow = window as unknown as Record<string, unknown>;
  if (probeWindow[INSTALLED_FLAG] || Math.random() >= PAGE_LOAD_SAMPLE_RATE) {
    return;
  }
  probeWindow[INSTALLED_FLAG] = true;

  const pending: Probe[] = [];
  let recorded = 0;
  let retries = 0;

  // Boot-time failures fire before PostHogProvider initializes, so hold them.
  const flush = () => {
    if (!pending.length) {
      return;
    }
    if (!posthog.__loaded) {
      if (retries++ < MAX_FLUSH_RETRIES) {
        setTimeout(flush, FLUSH_RETRY_MS);
      }
      return;
    }
    pending
      .splice(0)
      .forEach((probe) => posthog.capture(EXCEPTION_PATH_PROBE_EVENT, probe));
  };

  const record = (probe: Probe) => {
    if (recorded >= MAX_PROBES_PER_PAGE_LOAD) {
      return;
    }
    recorded += 1;
    pending.push(probe);
    flush();
  };

  try {
    window.addEventListener(
      'unhandledrejection',
      (event) => {
        if (event.reason instanceof Error) {
          return;
        }
        record(
          describe('unhandledrejection', event.reason, event.defaultPrevented),
        );
      },
      true,
    );

    window.addEventListener(
      'error',
      (event) => {
        // Resource load failures (images, scripts) also fire `error` here.
        if (event.target !== window || event.error instanceof Error) {
          return;
        }
        record(describe('error', event.error, event.defaultPrevented));
      },
      true,
    );
  } catch {
    // Diagnostics must never take the app down.
  }
};

installExceptionPathProbe();
